// ResearchSuggestions — the Suggestions review inbox at /partner/research/suggestions.
//
// One page governs every MACHINE-PROPOSED write to the knowledge graph / predictions ledger /
// drift queue / evidence attachments (doc §4). Nothing here auto-commits: each row is a
// research_capture_suggestions row (status='proposed'); the analyst amends-then-accepts (edits
// flow into the decide RPC's `_overrides`) or rejects with a reason. The security boundary is the
// SECURITY DEFINER research_capture_suggestion_decide RPC — the in-page author gate is UI-only.
//
// Conventions cloned from ResearchTriage.tsx + ResearchDrift.tsx: KPI card strip, type Tabs with
// count badges, a status sub-filter + free-text Input, a shadcn Table in a Card, poll every 10s.
// A per-kind diff-preview panel ("what will be written if you accept") opens in a Dialog.
//
// The decide RPC returns {ok, reason, current} exactly like mobile_approve_item:
//   reason 'accept'|'reject'         → applied; drop/refresh the row
//   reason 'already_applied'         → someone decided it elsewhere; refresh
//   reason 'stale'                   → optimistic-concurrency loss; re-seed updated_at from
//                                      `current` and re-prompt (never silently clobber)
//   reason 'not_found'               → gone; refresh
//   error.code '42501'               → "Not allowed" inline (author gate failed server-side)
//
// Labels: all enums go through src/components/research-hub/labels.ts (suggestionType /
// suggestionStatus / confidenceTier) — never raw snake_case / UUIDs.
//
// The research_capture_suggestions table + decide RPC are NEW (DRAFT SQL, m22) so generated
// types are stale → the table read and the RPC are cast through `as any`; no types regenerated.

import { useMemo, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Loader2, Inbox, ShieldAlert, TimerReset, Sparkles, Check, X, Eye,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { useResearchCapability } from "@/hooks/useResearchCapability";
import {
  suggestionType, suggestionStatus, confidenceTier, toneSurface, humanise,
  truncateLabel,
} from "@/components/research-hub/labels";

// ---------------------------------------------------------------------------
// Types (research_capture_suggestions — DRAFT m22 shape; generated types stale)
// ---------------------------------------------------------------------------

type SuggestionType =
  | "kg_node" | "kg_edge" | "thesis_attachment" | "macro_prediction" | "drift_flag" | "table_data";
type SuggestionStatus = "proposed" | "accepted" | "rejected" | "superseded" | "expired";

interface Suggestion {
  id: string;
  suggestion_type: SuggestionType;
  payload: Record<string, any>;
  title: string | null;
  rationale: string | null;
  source_id: string | null;
  confidence: number | null;
  status: SuggestionStatus;
  proposer_fn: string | null;
  proposer_model: string | null;
  decided_by: string | null;
  decided_at: string | null;
  decision_reason: string | null;
  resulting_entity_kind: string | null;
  resulting_entity_id: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

// decide RPC envelope ({ok, reason, current}). `current` is the full refreshed row on stale/applied.
interface DecideResult {
  ok?: boolean;
  reason?: "accept" | "reject" | "already_applied" | "stale" | "not_found" | string;
  resulting_entity_kind?: string | null;
  resulting_entity_id?: string | null;
  current?: (Partial<Suggestion> & { updated_at?: string | null }) | null;
}

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

const TYPE_TABS: { value: "all" | SuggestionType; label: string }[] = [
  { value: "all", label: "All" },
  { value: "kg_edge", label: "Relationships" },
  { value: "kg_node", label: "Entities" },
  { value: "thesis_attachment", label: "Attachments" },
  { value: "macro_prediction", label: "Predictions" },
  { value: "drift_flag", label: "Drift" },
  { value: "table_data", label: "Tables" },
];

const STATUS_FILTERS: { value: SuggestionStatus | "all"; label: string }[] = [
  { value: "proposed", label: "Awaiting you" },
  { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Rejected" },
  { value: "expired", label: "Expired" },
  { value: "all", label: "All statuses" },
];

function isExpiringSoon(expires_at: string | null): boolean {
  if (!expires_at) return false;
  const t = new Date(expires_at).getTime();
  if (Number.isNaN(t)) return false;
  const days = (t - Date.now()) / 86_400_000;
  return days > 0 && days <= 3;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function ResearchSuggestions() {
  const [typeTab, setTypeTab] = useState<"all" | SuggestionType>("all");
  const [statusFilter, setStatusFilter] = useState<SuggestionStatus | "all">("proposed");
  const [q, setQ] = useState("");
  const [previewing, setPreviewing] = useState<Suggestion | null>(null);
  const qc = useQueryClient();

  const { data: canAuthor } = useResearchCapability("author");

  const { data: rows, isLoading } = useQuery({
    queryKey: ["research_capture_suggestions"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("research_capture_suggestions")
        .select(
          "id,suggestion_type,payload,title,rationale,source_id,confidence,status,proposer_fn,proposer_model,decided_by,decided_at,decision_reason,resulting_entity_kind,resulting_entity_id,expires_at,created_at,updated_at",
        )
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as Suggestion[];
    },
    refetchInterval: 10_000,
  });

  // Type counts are computed on the PROPOSED set — the tab badges answer "how many await me
  // of each kind", not "how many exist in any status".
  const typeCounts = useMemo(() => {
    const c: Record<string, number> = { all: 0 };
    TYPE_TABS.forEach((t) => { if (t.value !== "all") c[t.value] = 0; });
    (rows ?? []).forEach((r) => {
      if (r.status !== "proposed") return;
      c.all++;
      c[r.suggestion_type] = (c[r.suggestion_type] ?? 0) + 1;
    });
    return c;
  }, [rows]);

  const kpis = useMemo(() => {
    const proposed = (rows ?? []).filter((r) => r.status === "proposed");
    const high = proposed.filter((r) => (r.confidence ?? 0) >= 0.75).length;
    const low = proposed.filter((r) => (r.confidence ?? 1) < 0.5).length;
    const expiring = proposed.filter((r) => isExpiringSoon(r.expires_at)).length;
    return { awaiting: proposed.length, high, low, expiring };
  }, [rows]);

  const filtered = useMemo(() => {
    let list = rows ?? [];
    if (statusFilter !== "all") list = list.filter((r) => r.status === statusFilter);
    if (typeTab !== "all") list = list.filter((r) => r.suggestion_type === typeTab);
    if (q.trim()) {
      const needle = q.toLowerCase();
      list = list.filter(
        (r) =>
          (r.title ?? "").toLowerCase().includes(needle) ||
          (r.rationale ?? "").toLowerCase().includes(needle) ||
          JSON.stringify(r.payload ?? {}).toLowerCase().includes(needle),
      );
    }
    return list;
  }, [rows, statusFilter, typeTab, q]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Suggestions</h1>
        <p className="text-sm text-muted-foreground max-w-2xl">
          Machine-proposed additions to the knowledge graph, predictions ledger, drift queue and
          evidence trail — captured by the browser extension or the ingest pipeline. Nothing is
          written until you accept it here. You can amend a proposal before accepting.
        </p>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Awaiting you</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Inbox className="h-5 w-5 text-[hsl(var(--financial-gold))]" />
              {kpis.awaiting}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>High confidence</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              {kpis.high}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Low confidence</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-muted-foreground" />
              {kpis.low}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Expiring soon</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              <TimerReset className="h-5 w-5 text-muted-foreground" />
              {kpis.expiring}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Type tabs + status sub-filter + free text */}
      <div className="space-y-3">
        <Tabs value={typeTab} onValueChange={(v) => setTypeTab(v as any)}>
          <TabsList className="flex-wrap h-auto">
            {TYPE_TABS.map((t) => (
              <TabsTrigger key={t.value} value={t.value}>
                {t.label}
                {typeCounts[t.value] != null && typeCounts[t.value] > 0 && (
                  <Badge variant="secondary" className="ml-2 h-4 text-[10px]">
                    {typeCounts[t.value]}
                  </Badge>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
            <SelectTrigger className="max-w-[12rem]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            placeholder="Filter by title, rationale or content…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="max-w-xs"
          />
        </div>
      </div>

      {!canAuthor && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          You can view these proposals but not action them — you need the author capability to
          accept or reject.
        </div>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">
            {filtered.length} suggestion{filtered.length === 1 ? "" : "s"}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-36">Proposed</TableHead>
                <TableHead className="w-40">Type</TableHead>
                <TableHead>What is proposed</TableHead>
                <TableHead className="w-36">Confidence</TableHead>
                <TableHead className="w-32">Status</TableHead>
                <TableHead className="w-24 text-right">Review</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                    Nothing waiting on you.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((s) => {
                const conf = confidenceTier(s.confidence);
                const st = suggestionStatus(s.status);
                return (
                  <TableRow key={s.id}>
                    <TableCell className="text-xs whitespace-nowrap text-muted-foreground align-top">
                      {format(new Date(s.created_at), "dd/MM/yyyy HH:mm")}
                      {isExpiringSoon(s.expires_at) && (
                        <div className="text-[10px] text-[hsl(38_60%_32%)] mt-0.5">expiring soon</div>
                      )}
                    </TableCell>
                    <TableCell className="align-top">
                      <Badge variant="outline" className="text-[10px]">
                        {suggestionType(s.suggestion_type)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs max-w-md align-top">
                      <div className="font-medium">{s.title ?? summariseSuggestion(s)}</div>
                      {s.rationale && (
                        <div className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2 italic">
                          “{s.rationale}”
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="align-top">
                      <span className={`inline-block rounded border px-1.5 py-0.5 text-[10px] ${toneSurface(conf.tone)}`}>
                        {conf.label}
                      </span>
                    </TableCell>
                    <TableCell className="align-top">
                      <span className={`inline-block rounded border px-1.5 py-0.5 text-[10px] ${toneSurface(st.tone)}`}>
                        {st.label}
                      </span>
                    </TableCell>
                    <TableCell className="text-right align-top">
                      <Button size="sm" variant="ghost" title="Review" onClick={() => setPreviewing(s)}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <ReviewPanel
        suggestion={previewing}
        canAuthor={!!canAuthor}
        onClose={() => setPreviewing(null)}
        onDecided={() => {
          setPreviewing(null);
          qc.invalidateQueries({ queryKey: ["research_capture_suggestions"] });
          qc.invalidateQueries({ queryKey: ["research_suggestions_pending_count"] });
        }}
        onReseed={(fresh) => setPreviewing(fresh)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// A one-line human summary of a suggestion's payload, for the list row when no title.
// ---------------------------------------------------------------------------
function summariseSuggestion(s: Suggestion): string {
  const p = s.payload ?? {};
  switch (s.suggestion_type) {
    case "kg_node":
      return `Add “${p.label ?? "an entity"}” (${humanise(p.node_type) || "entity"})`;
    case "kg_edge":
      return `${p?.from?.label ?? "?"} → ${p?.to?.label ?? "?"} (${humanise(p.edge_type) || "relationship"})`;
    case "thesis_attachment":
      return `Attach evidence to ${humanise(p.target_kind) || "a target"}`;
    case "macro_prediction":
      return `${p.organisation ?? "A house"} forecast for ${p.fred_series_id ?? "a series"}`;
    case "drift_flag":
      return p.summary ?? "New evidence contradicts a held claim";
    case "table_data":
      return `Table: ${p.table_title ?? "extracted data"}`;
    default:
      return "A proposed change";
  }
}

// ===========================================================================
// Review / diff-preview panel — "what will be written if you accept"
// ===========================================================================

interface ReviewPanelProps {
  suggestion: Suggestion | null;
  canAuthor: boolean;
  onClose: () => void;
  onDecided: () => void;
  onReseed: (fresh: Suggestion) => void;
}

function ReviewPanel({ suggestion, canAuthor, onClose, onDecided, onReseed }: ReviewPanelProps) {
  // Local overrides the analyst edits before accepting — merged over the payload server-side.
  // Keyed by the suggestion id so switching rows resets the draft.
  const [overrides, setOverrides] = useState<Record<string, any>>({});
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState<null | "accept" | "reject">(null);

  useEffect(() => {
    setOverrides({});
    setReason("");
    setRejecting(false);
  }, [suggestion?.id]);

  if (!suggestion) return null;

  const eff = { ...(suggestion.payload ?? {}), ...overrides };

  async function decide(decision: "accept" | "reject", reasonText?: string) {
    if (!suggestion || busy) return;
    setBusy(decision);
    try {
      const { data, error } = await (supabase.rpc as any)("research_capture_suggestion_decide", {
        _suggestion_id: suggestion.id,
        _expected_updated_at: suggestion.updated_at,
        _decision: decision,
        _reason: reasonText || null,
        _overrides: decision === "accept" ? overrides : {},
      });

      if (error) {
        // 42501 = author gate failed server-side (the security boundary). Surface inline.
        const denied = (error as { code?: string }).code === "42501";
        toast.error(
          denied
            ? "Not allowed — you don’t have permission to action this suggestion."
            : error.message ?? "Couldn’t apply the decision.",
        );
        return;
      }

      const res = (data ?? {}) as DecideResult;
      const r = res.reason;

      if (r === "accept" || r === "reject" || r === "already_applied") {
        toast.success(
          r === "already_applied"
            ? "Already decided elsewhere."
            : decision === "accept"
              ? "Accepted — written to the graph."
              : "Rejected.",
        );
        onDecided();
        return;
      }

      // Optimistic-concurrency loss: re-seed updated_at from the fresh row and re-prompt.
      if (r === "stale") {
        const fresh = res.current;
        toast.error("This changed elsewhere — refreshed. Review again and retry.");
        if (fresh && typeof fresh.updated_at === "string") {
          onReseed({ ...suggestion, ...(fresh as Partial<Suggestion>) } as Suggestion);
        } else {
          onDecided();
        }
        return;
      }

      if (r === "not_found") {
        toast.error("This suggestion has gone.");
        onDecided();
        return;
      }

      // Unknown reason (or a RAISE surfaced as ok:false without a mapped reason) — keep the row.
      toast.error(
        typeof r === "string" && r ? humanise(r) : "Unexpected response — please try again.",
      );
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn’t reach the server. Try again.");
    } finally {
      setBusy(null);
    }
  }

  const st = suggestionStatus(suggestion.status);
  const conf = confidenceTier(suggestion.confidence);
  const decided = suggestion.status !== "proposed";

  return (
    <>
      <Dialog open={!!suggestion} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {suggestionType(suggestion.suggestion_type)}
              <span className={`inline-block rounded border px-1.5 py-0.5 text-[10px] ${toneSurface(st.tone)}`}>
                {st.label}
              </span>
            </DialogTitle>
            <DialogDescription>
              {decided
                ? "This has already been decided — shown for reference."
                : "This is what will be written if you accept. Amend any field first if needed."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 text-sm">
            {suggestion.rationale && (
              <div className="rounded-md border bg-muted/40 p-2">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                  Why it was proposed
                </div>
                <p className="text-xs leading-relaxed">{suggestion.rationale}</p>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className={`inline-block rounded border px-1.5 py-0.5 ${toneSurface(conf.tone)}`}>
                {conf.label}
              </span>
              {suggestion.proposer_fn && <span>via {humanise(suggestion.proposer_fn)}</span>}
              {suggestion.proposer_model && <span>· {suggestion.proposer_model}</span>}
            </div>

            {/* Per-kind diff preview + inline amend controls */}
            <DiffPreview
              suggestion={suggestion}
              eff={eff}
              editable={!decided && canAuthor}
              onOverride={(patch) => setOverrides((prev) => ({ ...prev, ...patch }))}
            />

            {decided && suggestion.decision_reason && (
              <div className="rounded-md border bg-muted/20 p-2">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                  Decision note
                </div>
                <p className="text-xs">{suggestion.decision_reason}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Close</Button>
            {!decided && (
              <>
                <Button
                  variant="ghost"
                  disabled={busy != null || !canAuthor}
                  onClick={() => setRejecting(true)}
                >
                  <X className="h-4 w-4 mr-1" /> Reject
                </Button>
                <Button
                  disabled={busy != null || !canAuthor}
                  onClick={() => decide("accept")}
                >
                  {busy === "accept" ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 mr-1" />
                  )}
                  Accept
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={rejecting} onOpenChange={(o) => !o && setRejecting(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject this suggestion?</AlertDialogTitle>
            <AlertDialogDescription>
              It won’t be written to the graph. A short reason helps the next reviewer (optional).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            placeholder="Why reject? e.g. 'Duplicate of an existing relationship' or 'Not a real forecast'"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy != null}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy != null}
              onClick={(e) => {
                e.preventDefault();
                setRejecting(false);
                void decide("reject", reason);
              }}
            >
              {busy === "reject" && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Reject
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ===========================================================================
// Per-kind diff preview (+ the few amend controls that flow into `_overrides`)
// ===========================================================================

interface DiffPreviewProps {
  suggestion: Suggestion;
  eff: Record<string, any>;
  editable: boolean;
  onOverride: (patch: Record<string, any>) => void;
}

function DiffPreview({ suggestion, eff, editable, onOverride }: DiffPreviewProps) {
  switch (suggestion.suggestion_type) {
    case "kg_edge":
      return <EdgePreview eff={eff} editable={editable} onOverride={onOverride} />;
    case "kg_node":
      return <NodePreview eff={eff} />;
    case "thesis_attachment":
      return <AttachmentPreview eff={eff} />;
    case "macro_prediction":
      return <PredictionPreview eff={eff} editable={editable} onOverride={onOverride} />;
    case "drift_flag":
      return <DriftPreview eff={eff} />;
    case "table_data":
      return <TablePreview eff={eff} />;
    default:
      return <RawPreview eff={eff} />;
  }
}

function PreviewFrame({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

// kg_edge — [From] --edge_type--> [To] + quote + new-vs-existing node flags.
// Accept requires resolved from/to node_id (the RPC RAISEs otherwise) — surfaced here so the
// analyst knows to resolve nodes first.
function EdgePreview({ eff, editable, onOverride }: {
  eff: Record<string, any>; editable: boolean; onOverride: (p: Record<string, any>) => void;
}) {
  const from = eff.from ?? {};
  const to = eff.to ?? {};
  const needsFrom = !from.node_id;
  const needsTo = !to.node_id;
  const quote = eff?.properties?.quote as string | undefined;
  return (
    <PreviewFrame title="New relationship in the knowledge graph">
      <div className="flex items-center gap-2 flex-wrap text-sm">
        <NodeChip label={from.label} nodeType={from.node_type} isNew={needsFrom} />
        <span className="text-muted-foreground text-xs">
          — {humanise(eff.edge_type) || "relates to"} →
        </span>
        <NodeChip label={to.label} nodeType={to.node_type} isNew={needsTo} />
      </div>
      {(needsFrom || needsTo) && (
        <p className="mt-2 text-[11px] text-[hsl(38_60%_32%)]">
          {needsFrom && needsTo
            ? "Both endpoints are new — resolve them to existing entities before accepting."
            : "One endpoint is new — resolve it to an existing entity before accepting."}
        </p>
      )}
      {quote && (
        <p className="mt-2 text-xs italic text-muted-foreground border-l-2 pl-2">“{quote}”</p>
      )}
      {editable && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px]">From entity id (resolve)</Label>
            <Input
              className="h-7 text-xs"
              placeholder="paste existing node id"
              defaultValue={from.node_id ?? ""}
              onChange={(e) =>
                onOverride({ from: { ...from, node_id: e.target.value.trim() || null } })
              }
            />
          </div>
          <div>
            <Label className="text-[10px]">To entity id (resolve)</Label>
            <Input
              className="h-7 text-xs"
              placeholder="paste existing node id"
              defaultValue={to.node_id ?? ""}
              onChange={(e) =>
                onOverride({ to: { ...to, node_id: e.target.value.trim() || null } })
              }
            />
          </div>
        </div>
      )}
    </PreviewFrame>
  );
}

function NodeChip({ label, nodeType, isNew }: { label?: string; nodeType?: string; isNew?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1 rounded border bg-muted/40 px-2 py-0.5 text-xs">
      <span className="font-medium">{truncateLabel(label, 24) || "unresolved"}</span>
      {nodeType && <span className="text-muted-foreground text-[10px]">{humanise(nodeType)}</span>}
      {isNew && (
        <span className="text-[9px] uppercase tracking-wide text-[hsl(38_60%_32%)]">new</span>
      )}
    </span>
  );
}

function NodePreview({ eff }: { eff: Record<string, any> }) {
  const aliases: string[] = Array.isArray(eff.aliases) ? eff.aliases : [];
  const merge = eff.resolved_existing_node_id;
  return (
    <PreviewFrame title={merge ? "Merge into an existing entity" : "New entity in the knowledge graph"}>
      <div className="text-sm font-medium">{eff.label ?? "Unnamed"}</div>
      <div className="text-xs text-muted-foreground">{humanise(eff.node_type) || "Entity"}</div>
      {aliases.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {aliases.map((a, i) => (
            <span key={i} className="rounded bg-muted px-1.5 py-0.5 text-[10px]">{a}</span>
          ))}
        </div>
      )}
      {merge && (
        <p className="mt-2 text-[11px] text-muted-foreground">
          Aliases will be merged into the existing entity rather than creating a duplicate.
        </p>
      )}
    </PreviewFrame>
  );
}

function AttachmentPreview({ eff }: { eff: Record<string, any> }) {
  const anchor = eff.block_anchor as Record<string, any> | undefined;
  return (
    <PreviewFrame title="Evidence attachment">
      <div className="text-sm">
        Attach the captured source to{" "}
        <span className="font-medium">{humanise(eff.target_kind) || "a target"}</span>
        {eff.relevance && (
          <span className="text-muted-foreground"> · as {humanise(eff.relevance).toLowerCase()}</span>
        )}
      </div>
      {eff.note && <p className="mt-2 text-xs text-muted-foreground">{eff.note}</p>}
      {anchor?.quote && (
        <div className="mt-2 rounded border-l-2 pl-2">
          <p className="text-xs italic">“{anchor.quote}”</p>
          {anchor.canonicalUrl && (
            <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{anchor.canonicalUrl}</p>
          )}
        </div>
      )}
    </PreviewFrame>
  );
}

// macro_prediction — the exact ledger row that will be inserted, with the FRED series shown for
// validation (the RPC RAISEs if the series id is unknown).
function PredictionPreview({ eff, editable, onOverride }: {
  eff: Record<string, any>; editable: boolean; onOverride: (p: Record<string, any>) => void;
}) {
  const rows: [string, any][] = [
    ["Organisation", eff.organisation],
    ["FRED series", eff.fred_series_id],
    ["Direction", humanise(eff.direction)],
    ["Predicted value", eff.predicted_value],
    ["Range", eff.predicted_value_lower != null || eff.predicted_value_upper != null
      ? `${eff.predicted_value_lower ?? "?"} – ${eff.predicted_value_upper ?? "?"}` : null],
    ["By date", eff.predicted_by_date],
  ];
  return (
    <PreviewFrame title="New row in the predictions ledger">
      <table className="w-full text-xs">
        <tbody>
          {rows.filter(([, v]) => v != null && v !== "").map(([k, v]) => (
            <tr key={k} className="border-b last:border-0">
              <td className="py-1 pr-3 text-muted-foreground align-top w-32">{k}</td>
              <td className="py-1 font-medium">{String(v)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {eff.verbatim_quote && (
        <p className="mt-2 text-xs italic text-muted-foreground border-l-2 pl-2">“{eff.verbatim_quote}”</p>
      )}
      {editable && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px]">Predicted value</Label>
            <Input
              className="h-7 text-xs"
              defaultValue={eff.predicted_value ?? ""}
              onChange={(e) => onOverride({ predicted_value: e.target.value.trim() || null })}
            />
          </div>
          <div>
            <Label className="text-[10px]">By date (YYYY-MM-DD)</Label>
            <Input
              className="h-7 text-xs"
              defaultValue={eff.predicted_by_date ?? ""}
              onChange={(e) => onOverride({ predicted_by_date: e.target.value.trim() || null })}
            />
          </div>
        </div>
      )}
    </PreviewFrame>
  );
}

// drift_flag — contradicted edge vs new evidence, side by side.
function DriftPreview({ eff }: { eff: Record<string, any> }) {
  return (
    <PreviewFrame title="Contradiction between a held claim and new evidence">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded border p-2">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Held claim</div>
          <p className="text-xs">{eff.claim_edge_id ? "An existing knowledge-graph relationship" : "—"}</p>
        </div>
        <div className="rounded border p-2">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">New evidence</div>
          <p className="text-xs">{eff.summary ?? "New captured evidence"}</p>
        </div>
      </div>
      {eff.stance && (
        <p className="mt-2 text-[11px] text-muted-foreground">
          Stance: <span className="font-medium">{humanise(eff.stance)}</span>
          {eff.severity && ` · ${humanise(eff.severity)} severity`}
        </p>
      )}
      <p className="mt-2 text-[11px] text-muted-foreground">
        Accepting acknowledges the drift alert; the deep resolve happens in the Drift queue.
      </p>
    </PreviewFrame>
  );
}

// table_data — an actual HTML table preview (textContent-safe via React).
function TablePreview({ eff }: { eff: Record<string, any> }) {
  const columns: string[] = Array.isArray(eff.columns) ? eff.columns : [];
  const dataRows: any[][] = Array.isArray(eff.rows) ? eff.rows : [];
  return (
    <PreviewFrame title={eff.table_title ? `Table: ${eff.table_title}` : "Extracted table"}>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border">
          {columns.length > 0 && (
            <thead>
              <tr className="bg-muted/50">
                {columns.map((c, i) => (
                  <th key={i} className="border px-2 py-1 text-left font-semibold">{c}</th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {dataRows.slice(0, 20).map((r, ri) => (
              <tr key={ri}>
                {(Array.isArray(r) ? r : [r]).map((cell, ci) => (
                  <td key={ci} className="border px-2 py-1">{String(cell ?? "")}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {dataRows.length > 20 && (
        <p className="mt-1 text-[10px] text-muted-foreground">
          Showing first 20 of {dataRows.length} rows.
        </p>
      )}
    </PreviewFrame>
  );
}

function RawPreview({ eff }: { eff: Record<string, any> }) {
  return (
    <PreviewFrame title="Proposed content">
      <pre className="text-[11px] whitespace-pre-wrap break-words text-muted-foreground">
        {JSON.stringify(eff, null, 2)}
      </pre>
    </PreviewFrame>
  );
}
